import { format } from 'date-fns';
import React, { useRef } from 'react';
import { Control, Controller } from 'react-hook-form';
import { Calendar } from 'shared/assets/icons';
import { Datepicker } from 'shared/components/ui/datepicker';
import { FormInput } from 'shared/components/ui/form-input';
import { PopupMenu, PopupMenuList } from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './form-date-input.style';

export type FormDateInputProps = Omit<
  React.ComponentProps<typeof Datepicker>,
  | 'onChange'
  | 'selected'
  | 'customInput'
  | 'popperProps'
  | 'wrapperClassName'
  | 'weekStartsOn'
  | 'placeholderText'
> & {
  control: Control<any>;
  name: string;
  error?: string;
  isSmall?: boolean;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  placeholderText?: string;
  minDate?: Date;
  maxDate?: Date;
  today: Date;
};

export const FormDateInput: React.FC<FormDateInputProps> = ({
  control,
  name,
  error,
  isSmall,
  dateFormat,
  weekStartsOn,
  placeholderText,
  today,
  ...rest
}) => {
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const ref = useRef<HTMLInputElement>(null);

  const handleDateChange = (
    date: Date | [Date | null, Date | null] | null,
    onChange: (date: Date | [Date | null, Date | null] | null) => void,
  ) => {
    onChange(date);
    closeMenu();
  };

  return (
    <Styled.Container>
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const onChangeDate = (
            date: Date | [Date | null, Date | null] | null,
          ) => handleDateChange(date, field.onChange);
          const onClear = () => field.onChange(null);

          return (
            <>
              <FormInput
                ref={ref}
                value={field.value ? format(field.value, dateFormat) : ''}
                onFocus={openMenu}
                onClick={openMenu}
                readOnly
                iconRight={Calendar}
                error={error}
                canClear
                onClear={onClear}
                isSmall={isSmall}
                placeholder={placeholderText}
              />
              {menuOpen && (
                <PopupMenu
                  referenceElement={ref}
                  onOutside={closeMenu}
                  noBackdrop
                  preventReferenceElementOverlap
                >
                  <PopupMenuList>
                    <Datepicker
                      wrapperClassName={Styled.datePickerWrapperClassName}
                      onChange={onChangeDate}
                      selected={field.value}
                      inline
                      weekStartsOn={weekStartsOn}
                      today={today}
                      {...rest}
                    />
                  </PopupMenuList>
                </PopupMenu>
              )}
            </>
          );
        }}
      />
    </Styled.Container>
  );
};
